import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  Gift, 
  Sparkles, 
  Users, 
  Star,
  Percent,
  Crown,
  ArrowRight,
  Copy,
  Check,
  Tag,
  Zap,
  Ticket,
  Award
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AuthContext } from "@/App";

// Icon mapping for dynamic icons
const ICON_MAP = {
  percent: Percent,
  users: Users,
  star: Star,
  crown: Crown,
  gift: Gift,
  tag: Tag,
  zap: Zap,
  ticket: Ticket,
  award: Award,
  sparkles: Sparkles
};

// Color mapping
const COLOR_MAP = {
  blue: "text-blue-600 bg-blue-100",
  green: "text-green-600 bg-green-100",
  emerald: "text-emerald-600 bg-emerald-100",
  amber: "text-amber-600 bg-amber-100",
  purple: "text-purple-600 bg-purple-100",
  red: "text-red-600 bg-red-100",
  orange: "text-orange-600 bg-orange-100",
  pink: "text-pink-600 bg-pink-100"
};

export default function RewardsDropdown() {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const referralCode = user ? `REBUSI${user.id?.slice(-6).toUpperCase() || '2026'}` : "REBUSI2026";
  
  useEffect(() => {
    if (open && user) {
      fetchOffers();
    }
  }, [open, user]);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/offers");
      setOffers(data);
    } catch (error) {
      console.error("Failed to fetch offers:", error);
      // Fall back to default offers if API fails
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code || referralCode);
    setCopied(true);
    toast.success("Code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  // Default offers when no offers from API
  const defaultOffers = [
    {
      id: "default-1",
      icon: "users",
      title: "Refer & Earn",
      description: "Get ₹500 for each friend who signs up",
      color: "blue",
      offer_type: "referral"
    },
    {
      id: "default-2",
      icon: "percent",
      title: "First Listing Free",
      description: "List your first business for free",
      color: "emerald",
      badge: "New User",
      offer_type: "free_listing"
    },
    {
      id: "default-3",
      icon: "star",
      title: "Premium Trial",
      description: "Try premium features for 7 days",
      color: "amber",
      badge: "Limited",
      offer_type: "premium_trial"
    },
    {
      id: "default-4",
      icon: "crown",
      title: "VIP Program",
      description: "Close 3 deals to unlock VIP perks",
      color: "purple",
      offer_type: "vip"
    }
  ];

  const displayOffers = offers.length > 0 ? offers : defaultOffers;

  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || Percent;
  };

  const getColorClass = (color) => {
    return COLOR_MAP[color] || COLOR_MAP.blue;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="rewards-gift"
        >
          <Gift className="w-5 h-5" />
          {(offers.length > 0 || !user) && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-80 p-0" align="end">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-4 rounded-t-lg">
          <div className="flex items-center gap-2 text-white">
            <Sparkles className="w-5 h-5" />
            <h3 className="font-semibold">Rewards & Offers</h3>
          </div>
          <p className="text-amber-100 text-xs mt-1">Exclusive deals just for you</p>
        </div>
        
        {/* Referral Code */}
        <div className="p-3 bg-amber-50 border-b border-amber-100">
          <p className="text-xs text-amber-700 mb-2">Your Referral Code:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white px-3 py-2 rounded-md text-sm font-mono font-semibold text-slate-900 border border-amber-200">
              {referralCode}
            </code>
            <Button 
              size="sm" 
              variant="outline"
              className="border-amber-300 hover:bg-amber-100"
              onClick={() => handleCopyCode(referralCode)}
              data-testid="copy-referral-code"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Offers List */}
        <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              Loading offers...
            </div>
          ) : displayOffers.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              No active offers right now
            </div>
          ) : (
            displayOffers.map((offer) => {
              const Icon = getIcon(offer.icon);
              const colorClass = getColorClass(offer.color);
              return (
                <div 
                  key={offer.id} 
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => {
                    if (offer.code) {
                      handleCopyCode(offer.code);
                    } else if (offer.link) {
                      window.location.href = offer.link;
                    }
                  }}
                  data-testid={`offer-${offer.id}`}
                >
                  <div className={`p-2 rounded-lg ${colorClass}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-900">{offer.title}</p>
                      {offer.badge && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {offer.badge}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">{offer.description}</p>
                    {offer.value && (
                      <p className="text-xs font-semibold text-emerald-600 mt-0.5">{offer.value}</p>
                    )}
                    {offer.code && (
                      <p className="text-xs text-blue-600 font-mono mt-0.5">Code: {offer.code}</p>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </div>
              );
            })
          )}
        </div>
        
        {/* Footer */}
        <div className="p-3 bg-slate-50 border-t border-slate-100 rounded-b-lg">
          <p className="text-xs text-slate-500 text-center">
            {offers.length > 0 
              ? `${offers.length} active offer${offers.length !== 1 ? 's' : ''}`
              : "Check back for more offers"}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
